<template>
  <TabelaOSD
    :colunas="resolvedColumns"
    :linhas="table.rows"
    :carregando="table.isLoading"
    :total-linhas="table.totalRows"
    :pagina="pagination.page"
    :campo-ordenacao="pagination.sort"
    :ordem-ordenacao="pagination.order"
    :texto-tabela-vazia="$t('indicadoresNaoEncontrados')"
    @page-change="onPageChange"
    @sort-change="onSortChange"
    @reload="loadFeedbackData"
  >
    <template #toolbar-actions>
      <FeedbackExport v-bind="pagination" :headers="headers" />
    </template>
    <template #toolbar-filters>
      <FeedbackFilters :loading="table.isLoading" @filter="onFilter" />
    </template>
    <template #cell-lido="props">
      <span>
        <button
          type="button"
          class="btn btn-primary mx-1"
          :title="props.linha.lido ? $t('marcarComoNaoLido') : $t('marcarComoLido')"
          @click="alternarLido(props.linha)"
        >
          <i :class="props.linha.lido ? 'bi bi-envelope-open-fill' : 'bi bi-envelope-fill'" />
        </button>
      </span>
    </template>
  </TabelaOSD>
</template>

<script>
import FeedbackExport from './FeedbackExport.vue';
import FeedbackFilters from './FeedbackFilters.vue';
import TabelaOSD from '@/modules/shared/components/tabelaOSD/TabelaOSD.vue';
import { getPaginatedFeedbackData, salvarComentario } from '../feedbackService';
import { useToast } from 'vue-toastification';
import { toDate } from '@/modules/core/utils/formats';
import { i18n } from '@/modules/localization';
import { computed } from 'vue';

export default {
  components: {
    FeedbackExport,
    FeedbackFilters,
    TabelaOSD,
  },

  data() {
    return {
      table: {
        totalRows: 0,
        rows: [],
        columns: [
          {
            label: computed(() => i18n.global.t('lido')),
            field: 'lido',
            tdClass: 'text-center',
            width: '100px',
          },
          {
            label: computed(() => i18n.global.t('nomeDoUsuario')),
            field: 'nomeUsuario',
          },
          {
            label: computed(() => i18n.global.t('emailDoUsuario')),
            field: 'emailUsuario',
          },
          {
            label: computed(() => i18n.global.t('dataHoraCriacao')),
            field: 'dataHoraCriacao',
            tdClass: 'text-break',
            formatFn: toDate,
          },
          {
            label: computed(() => i18n.global.t('texto')),
            field: 'texto',
          },
        ],
        mode: 'remote',
        class: 'osd-table',
        styleClass: 'vgt-table bordered condensed',
        compactMode: true,
        lineNumbers: true,
        paginationOptions: {
          enabled: true,
          mode: 'pages',
          perPageDropdownEnabled: false,
          nextLabel: '>',
          prevLabel: '<',
          infoFn: (params) => `${params.firstRecordOnPage} - ${params.lastRecordOnPage} de ${params.totalRecords}`,
        },
        sortOptions: {
          enabled: true,
          initialSortBy: { field: 'dataHoraCriacao', type: 'desc' },
        },
        isLoading: false,
      },
      pagination: {
        page: 1,
        sort: 'dataHoraCriacao',
        order: 'desc',
        search: '',
      },
      toast: useToast(),
    };
  },

  computed: {
    resolvedColumns() {
      return this.table.columns.map((column) => ({
        ...column,
        label: column.label?.value ?? column.label,
      }));
    },
    headers() {
      return this.resolvedColumns.map((column) => column.label);
    },
  },

  mounted() {
    this.loadFeedbackData();
  },

  methods: {
    updateParams(pagination) {
      this.pagination = { ...this.pagination, ...pagination };
    },

    onPageChange(page) {
      this.updateParams({ page });
      this.loadFeedbackData();
    },

    onSortChange(field) {
      const nextOrder = this.pagination.sort === field && this.pagination.order === 'asc' ? 'desc' : 'asc';
      this.updateParams({
        page: 1,
        sort: field,
        order: nextOrder,
      });
      this.loadFeedbackData();
    },

    onFilter(filters) {
      this.updateParams({ ...filters, page: 1 });
      this.loadFeedbackData();
    },

    async loadFeedbackData() {
      this.table.isLoading = true;
      try {
        const data = await getPaginatedFeedbackData(this.pagination);
        this.table.rows = data.dados;
        this.table.totalRows = data.total;
      } catch (error) {
        this.toast.error(i18n.global.t('erroAoObterAsInfosDosComentarios'));
      } finally {
        this.table.isLoading = false;
      }
    },

    async alternarLido(comentario) {
      const originalState = comentario.lido;

      try {
        comentario.lido = !comentario.lido;

        await salvarComentario(comentario);
        this.toast.success(i18n.global.t('comentarioSalvoComSucesso'));
        await this.loadFeedbackData();
      } catch (error) {
        comentario.lido = originalState;
        this.toast.error(i18n.global.t('falhaAoSalvarComentario'));
      }
    },
  },
};
</script>
